import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { IntlProvider } from 'react-intl'
import { logWarning } from '@/services/errorService'

import { PageLoader } from '@/components/common/PageLoader'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/constants/localeConstants'
import enUSMessages from '../translations/en-US.json'

const LOCALE_MODULES: Record<string, () => Promise<{ default: Record<string, string> }>> = {
	'en-US': () => import('../translations/en-US.json'),
	'lt-LT': () => import('../translations/lt-LT.json'),
}

async function loadMessages(locale: string): Promise<Record<string, string>> {
	try {
		const loader = LOCALE_MODULES[locale] ?? LOCALE_MODULES[DEFAULT_LOCALE]
		if (!loader) return enUSMessages
		const module = await loader()
		return module.default
	} catch (error) {
		logWarning('[I18nProvider] Failed to load translation module, falling back to en-US', {
			locale,
			error,
		})
		return enUSMessages
	}
}

function detectLocale(): string {
	const searchParams = new URLSearchParams(window.location.search)
	const queryLang = searchParams.get('lang')
	if (queryLang && SUPPORTED_LOCALES.includes(queryLang as (typeof SUPPORTED_LOCALES)[number])) {
		return queryLang
	}

	const browserLang = navigator.language
	if (SUPPORTED_LOCALES.includes(browserLang as (typeof SUPPORTED_LOCALES)[number])) {
		return browserLang
	}

	const langPrefix = browserLang.split('-')[0]
	const match = SUPPORTED_LOCALES.find((l) => l.startsWith(langPrefix))
	return match ?? DEFAULT_LOCALE
}

interface I18nContextValue {
	locale: string
	changeLocale: (locale: string) => void
}

const I18nContext = createContext<I18nContextValue>({
	locale: DEFAULT_LOCALE,
	changeLocale: () => undefined,
})

export function useLocale(): I18nContextValue {
	return useContext(I18nContext)
}

interface I18nProviderProps {
	children: React.ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
	const [locale, setLocale] = useState<string>(() => detectLocale())
	const [messages, setMessages] = useState<Record<string, string> | null>(null)

	useEffect(() => {
		void loadMessages(locale)
			.then(setMessages)
			.catch((error: unknown) => {
				logWarning(
					'[I18nProvider] Unexpected error loading messages, falling back to en-US',
					{
						locale,
						error,
					},
				)
				setMessages(enUSMessages)
			})
	}, [locale])

	const changeLocale = useCallback((newLocale: string) => {
		if (SUPPORTED_LOCALES.includes(newLocale as (typeof SUPPORTED_LOCALES)[number])) {
			setLocale(newLocale)
		}
	}, [])

	const contextValue = useMemo(() => ({ locale, changeLocale }), [locale, changeLocale])

	if (!messages) {
		return <PageLoader />
	}

	return (
		<I18nContext.Provider value={contextValue}>
			<IntlProvider locale={locale} messages={messages} defaultLocale={DEFAULT_LOCALE}>
				{children}
			</IntlProvider>
		</I18nContext.Provider>
	)
}
